// @story HIR-SYS1
// Hiring's own app for one school: hiring.<school>.equaos.in (HIR-SYS1).
//
// Every module is a complete app at <module>.<school>.equaos.in as well as a
// page in the ERP (Sanjoy, 3 Oct 2026). This is the session for Hiring's,
// wired the way @equaos/frame does it (DPS-ERP-Console docs/EQUAOS-FRAME.md),
// because the frame mounts Vite remotes and this app is not one yet:
//
//   1. No session: the browser goes to the school's own sign-in,
//      https://<school>/sign-in?return=<this address>. There is no sign-in
//      form here, ever (one sign-in, Sanjoy 3 Oct 2026).
//   2. The school signs the person in and sends them back with
//      ?handoff=<code>. The code is redeemed at the gateway
//      (POST /auth/handoff, same origin; it only works at this address) and
//      taken out of the address bar at once.
//   3. The session lives here: the access token where every screen already
//      reads it (localStorage 'token'), the refresh token in this tab's
//      sessionStorage, as the frame keeps it. Renewal goes to the gateway at
//      /auth/refresh on this address.
//   4. Sign-out ends this address's session, then the school's
//      (/sign-out?return=…), which shows its sign-in ready to come back.
//   5. A session lost mid-way starts the same trip again. A second trip for
//      the same page within 30 s is a loop: it stops and says so, with a
//      Try again button, and still shows no form.
//
// The school is the address's (/v1/hosts/here), and the token is that
// school's: the API refuses another school's token on this address and reads
// only this school here, administrators included.
//
// Candidate screens never need any of this: a candidate opens their link as
// it is, with no sign-in.

import { SCHOOL_ADDRESS, API_ROOT } from './config/base';
import { isPublicRoute } from './shell';

export const standalone = Boolean(SCHOOL_ADDRESS);

const REFRESH_KEY = 'hiring.refresh';
const TRIP_KEY = 'hiring.signin.trip';
const TRIP_WINDOW_MS = 30000;

/* Leaving the page goes through here: one seam, so tests can see where to. */
export const browser = { go: (url) => window.location.assign(url) };

let school = null;      // { slug, name } from /v1/hosts/here
let leaving = false;

const store = {
  get(area, key) { try { return window[area].getItem(key); } catch { return null; } },
  set(area, key, value) {
    try {
      if (value === null || value === undefined) window[area].removeItem(key);
      else window[area].setItem(key, value);
    } catch { /* no storage: this page only */ }
  },
};

export function schoolName() {
  return (school && school.name) || '';
}

/* The tab names the app, then the school once the address has said which:
   one bundle serves every school, so the page itself names none. */
export const titleFor = (name) => (name ? `Hiring · ${name}` : 'Hiring · EquaOS');

/* ── Where to go ───────────────────────────────────────────────────────── */

export const signInAddress = (schoolAddress, back) =>
  `${schoolAddress}/sign-in?return=${encodeURIComponent(back)}`;
export const signOutAddress = (schoolAddress, back) =>
  `${schoolAddress}/sign-out?return=${encodeURIComponent(back)}`;

/* Whether to make the trip to sign-in now, given the last one ({ path, at }
   or null). Another trip within 30 s, from any page of this app, means
   signing in did not stick: going round again would loop for ever.
   (AUT-TC2, 15 Oct 2026: a platform owner switched into Hinjawadi went round
   about 25 times in a minute, landing on a different page each time.) */
export function tripDecision(previous, path, now = Date.now()) {
  const recent = previous && typeof previous === 'object'
    && typeof previous.at === 'number'
    && now - previous.at >= 0
    && now - previous.at < TRIP_WINDOW_MS;
  if (recent) return { go: false, record: previous };
  return { go: true, record: { path, at: now } };
}

/* The hand-off code, and the address without it. */
export function readHandoff(href) {
  let url;
  try { url = new URL(href); } catch { return { code: null, path: null }; }
  const code = url.searchParams.get('handoff');
  if (code === null) return { code: null, path: null };
  url.searchParams.delete('handoff');
  return { code: code || null, path: url.pathname + (url.searchParams.toString() ? `?${url.searchParams}` : '') + url.hash };
}

/* ── The session ───────────────────────────────────────────────────────── */

function setSession({ accessToken, refreshToken }) {
  store.set('localStorage', 'token', accessToken || null);
  if (refreshToken !== undefined) store.set('sessionStorage', REFRESH_KEY, refreshToken || null);
  store.set('localStorage', 'refreshToken', null); // never here on this address
}

function clearSession() {
  store.set('localStorage', 'token', null);
  store.set('localStorage', 'user', null);
  store.set('localStorage', 'refreshToken', null);
  store.set('sessionStorage', REFRESH_KEY, null);
}

async function gateway(path, { method = 'GET', body, token } = {}) {
  const headers = {};
  if (body !== undefined) headers['content-type'] = 'application/json';
  if (token) headers.authorization = `Bearer ${token}`;
  const res = await fetch(path, {
    method, headers, credentials: 'same-origin',
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error((data && data.error && data.error.message) || data.message || `HTTP ${res.status}`);
    err.status = res.status;
    err.code = data && data.error && data.error.code;
    throw err;
  }
  return data;
}

/* One renewal at a time: the gateway rotates the refresh token, and spending
   the same one twice looks like a stolen token and ends the session. */
let refreshing = null;

export function refresh() {
  if (!refreshing) {
    refreshing = (async () => {
      const refreshToken = store.get('sessionStorage', REFRESH_KEY);
      if (!refreshToken) throw new Error('no session to renew');
      const data = await gateway('/auth/refresh', { method: 'POST', body: { refreshToken } });
      if (!data.accessToken) throw new Error('renewal returned no token');
      setSession(data);
      return data.accessToken;
    })().finally(() => { refreshing = null; });
  }
  return refreshing;
}

/* Who is signed in, as the `user` every screen reads. */
async function loadUser() {
  const call = (token) => gateway(`${API_ROOT}/api/auth/me`, { token });
  let res;
  try {
    res = await call(store.get('localStorage', 'token'));
  } catch (err) {
    if (err.status !== 401) throw err;
    res = await call(await refresh());
  }
  const me = res.data || {};
  const user = {
    id: me.id,
    email: me.email || '',
    name: me.name || me.email || '',
    role: me.role === 'admin' ? 'admin' : 'user',
    companyName: schoolName(),
    tenant: (school && school.slug) || '',
  };
  store.set('localStorage', 'user', JSON.stringify(user));
  return user;
}

/* ── Trips to the school ───────────────────────────────────────────────── */

let onStuck = () => {};
let stuckMessage = null;

/* What the page shows when a trip mid-session would loop (it renders the
   message with a Try again). */
export function whenStuck(fn) {
  onStuck = typeof fn === 'function' ? fn : () => {};
}

/* To the school's sign-in, and back here. Returns false when it would loop,
   having told the page to say so. */
export function goSignIn() {
  if (leaving) return true;
  let previous = null;
  try { previous = JSON.parse(store.get('sessionStorage', TRIP_KEY) || 'null'); } catch { previous = null; }
  const decision = tripDecision(previous, window.location.pathname, Date.now());
  store.set('sessionStorage', TRIP_KEY, JSON.stringify(decision.record));
  clearSession();
  if (!decision.go) {
    stuckMessage = "Signing in for Hiring did not complete. Try again, or open Hiring from your school's address.";
    onStuck(stuckMessage);
    return false;
  }
  leaving = true;
  browser.go(signInAddress(SCHOOL_ADDRESS, window.location.href));
  return true;
}

/* Try again after a stuck trip: forget the last one, and go. */
export function retrySignIn() {
  store.set('sessionStorage', TRIP_KEY, null);
  goSignIn();
}

/* Sign out here, then at the school. */
export async function signOut() {
  const refreshToken = store.get('sessionStorage', REFRESH_KEY);
  if (refreshToken) {
    try { await gateway('/auth/logout', { method: 'POST', body: { refreshToken } }); } catch { /* signing out here matters more */ }
  }
  clearSession();
  leaving = true;
  browser.go(signOutAddress(SCHOOL_ADDRESS, `${window.location.origin}/`));
}

/* ── Boot ─────────────────────────────────────────────────────────────── */

/* Called once before the first render on the school's Hiring address.
   Resolves to null when the screens can render, to a message when they
   cannot (shown with a Try again, never a form), and never resolves when the
   browser is on its way to the school's sign-in. */
export async function bootStandalone() {
  const route = window.location.pathname.replace(/^\/hiring(?=\/|$)/, '') || '/';

  const { code, path } = readHandoff(window.location.href);
  // Single use, and ours alone: never left in the bar, the history or a bookmark.
  if (code !== null) window.history.replaceState(window.history.state, '', path);

  try {
    const here = await gateway('/v1/hosts/here');
    school = here && here.tenant ? { slug: here.tenant.slug, name: here.tenant.name } : null;
  } catch { school = null; }
  document.title = titleFor(schoolName());

  if (isPublicRoute(route)) return null;

  if (code) {
    try {
      const data = await gateway('/auth/handoff', { method: 'POST', body: { code } });
      if (data.mfaRequired) throw Object.assign(new Error('two-step needed'), { code: 'mfa_required' });
      if (!data.accessToken) throw new Error('no session in the hand-off');
      setSession(data);
    } catch (err) {
      clearSession();
      return err.code === 'mfa_required'
        ? 'Your role at this school needs two-step sign-in. Try again to sign in at your school.'
        : 'That sign-in link has expired or was already used. Try again to sign in at your school.';
    }
  }

  if (store.get('localStorage', 'token') || store.get('sessionStorage', REFRESH_KEY)) {
    try {
      await loadUser();
      /* The last trip is kept, not cleared, on a good sign-in (AUT-TC2): if
         this session is refused straight away, the next trip within 30 s
         stops with a message instead of going round again. A trip later
         than that goes as normal. */
      return null;
    } catch (err) {
      if (err.status === 403) {
        clearSession();
        return err.message || 'This session belongs to another school.';
      }
      /* Hiring or the gateway is down, not the session: going to sign in
         would not help, and would come straight back. */
      if (err.status && err.status !== 401) {
        return 'Hiring could not be reached just now. Try again in a moment.';
      }
      /* The session is over (or there is none to renew): sign in again. */
    }
  }

  if (!goSignIn()) return stuckMessage;
  return new Promise(() => {}); // leaving for the school's sign-in
}

/* ── Switching school (T2 parity, Shohini 9 Oct 2026) ─────────────────────
   Someone with a login at more than one school (or a group administrator)
   picks another school in the sidebar's school card and lands on THAT
   school's Hiring app, signed in, with no password retyped.

   The gateway decides who may go where (/auth/profiles, /auth/switch). As
   the EquaOS frame does it since 1.6.0 (C2, 29 Oct 2026): one /auth/switch
   with this address's refresh token, `handoff`, the app's name ('hiring')
   and the page to land on. The gateway builds that school's Hiring address
   itself (Auth #116), checks it is the other school's, and answers with a
   one-time hand-off there; the browser goes, and that address redeems it as
   any sign-in (bootStandalone). Hiring goes only to another school's Hiring
   address; a school without one is said, and nothing moves. This address's
   own session is left as it is. */

/* The schools this person can switch to: [{ tenant, name, current }]. */
export async function schoolProfiles() {
  const call = (token) => gateway('/auth/profiles', { token });
  let data;
  try {
    data = await call(store.get('localStorage', 'token'));
  } catch (err) {
    if (err.status !== 401) throw err;
    data = await call(await refresh());
  }
  return Array.isArray(data && data.profiles) ? data.profiles : [];
}

/* This app's name at the gateway: it lives at hiring.<school address>. */
const APP = 'hiring';
/* Another school's Hiring address: hiring.<school>.equaos.in. */
const isHiringAddress = (host) => /^hiring\.[a-z0-9-]+(\.[a-z0-9-]+)*\.equaos\.in$/i.test(String(host || ''));

/* Where to land at the other school: the same section (a sidebar page),
   never a record of this school (a vacancy or a candidate there is another). */
export function landingPath(pathname, sections) {
  const p = String(pathname || '/').replace(/^\/hiring(?=\/|$)/, '') || '/';
  const hit = (sections || []).find((s) => p === s || p.startsWith(`${s}/`));
  return hit || '/dashboard';
}

/* At most one switch per page load (AUT-TC2): a second one before the page
   has left is a loop, not a person choosing. And never to the school this
   address already is. */
let switchedThisLoad = false;

export async function switchToSchool(slug, landOn = '/dashboard') {
  if (school && school.slug && slug === school.slug) return false;
  if (switchedThisLoad || leaving) return false;
  switchedThisLoad = true;
  try {
    return await switchOnce(slug, landOn);
  } catch (err) {
    switchedThisLoad = false; // a refusal the person can read and try again
    throw err;
  }
}

async function switchOnce(slug, landOn) {
  if (refreshing) { try { await refreshing; } catch { /* use what is stored */ } }
  const refreshToken = store.get('sessionStorage', REFRESH_KEY);
  if (!refreshToken) throw new Error('Your session has ended. Sign in again, then switch.');

  const { redirect } = await gateway('/auth/switch', {
    method: 'POST', body: { refreshToken, tenant: slug, handoff: true, app: APP, path: landOn },
  });
  let next = null;
  try { next = new URL(redirect); } catch { next = null; }
  if (!next || next.protocol !== 'https:') throw new Error('The switch could not be completed. Try again.');
  // The gateway sends a school without a Hiring app to its own address.
  if (!isHiringAddress(next.hostname)) throw new Error('That school has no Hiring app of its own yet.');
  leaving = true;
  browser.go(next.toString());
  return true;
}
